import React, { useEffect, useMemo, useState } from "react";
import { Copy, PaperPlaneTilt, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { buildEmailDraft, buildGmailComposeUrl, buildMailtoUrl, isMobileDevice } from "./callListHelpers";
import { teleproApi } from "../../lib/teleproApi";

/**
 * EmailPreviewModal — overlay shown when the telepro clicks "Prévisualiser & envoyer email".
 *
 * - Pre-fills subject + body from `buildEmailDraft({biz, telepro, contactName})`.
 * - Both fields are editable so the telepro can tweak the tone.
 * - "Réinitialiser" rolls back to the canonical template at any time.
 * - "Envoyer via Gmail" opens the Gmail compose URL in a new tab, then calls
 *   onSent() so the parent can mark contact_method=email.
 * - Esc / backdrop click closes the modal.
 */
export default function EmailPreviewModal({ biz, telepro, contactName, contactEmail, onClose, onSent }) {
  const initial = useMemo(() => buildEmailDraft({ biz, telepro, contactName }), [biz, telepro, contactName]);
  const [subject, setSubject] = useState(initial.subject);
  const [body, setBody] = useState(initial.body);
  const proEmail = telepro?.pro_email || telepro?.email || "";

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const reset = () => {
    const fresh = buildEmailDraft({ biz, telepro, contactName });
    setSubject(fresh.subject);
    setBody(fresh.body);
  };

  const [sending, setSending] = useState(false);
  const mobile = isMobileDevice();

  const sendDirect = async () => {
    setSending(true);
    try {
      await teleproApi.post("/me/send-email", { business_id: biz.id, to: contactEmail, subject, body });
      toast.success(`Email envoyé à ${contactEmail}`);
      onSent && onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "L'envoi a échoué, essaie via Gmail");
    } finally {
      setSending(false);
    }
  };

  const openMailApp = () => {
    const args = { to: contactEmail, subject, body };
    if (mobile) window.location.href = buildMailtoUrl(args);
    else window.open(buildGmailComposeUrl(args), "_blank", "noopener,noreferrer");
    toast.message("N'oublie pas de cliquer sur « Envoyer » dans ta messagerie");
    onClose && onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-foreground/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      data-testid={`email-preview-modal-${biz.id}`}
    >
      <div className="bg-card border border-border rounded-sm shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-border bg-primary/5">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-7 h-7 grid place-items-center rounded-sm bg-primary/15 text-primary shrink-0">
              <PaperPlaneTilt size={14} weight="duotone"/>
            </span>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Prévisualiser & modifier</div>
              <div className="font-display text-sm font-semibold truncate">{biz.name}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1"
            aria-label="Fermer"
            data-testid={`email-preview-close-${biz.id}`}
          >
            <X size={16}/>
          </button>
        </div>

        <div className="overflow-y-auto px-4 sm:px-5 py-4 space-y-3 flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <div className="bg-muted/40 border border-border rounded-sm px-2.5 py-2">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-0.5">Réponses reçues sur</div>
              <div className="font-mono truncate" data-testid={`email-preview-from-${biz.id}`}>{proEmail || "—"}</div>
            </div>
            <div className="bg-muted/40 border border-border rounded-sm px-2.5 py-2">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-0.5">À</div>
              <div className="font-mono truncate" data-testid={`email-preview-to-${biz.id}`}>{contactEmail}</div>
            </div>
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground block mb-1">Objet</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full text-sm border border-border rounded-sm px-2.5 py-2 bg-background focus:border-primary outline-none"
              data-testid={`email-preview-subject-${biz.id}`}
              maxLength={200}
            />
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground block mb-1">Message</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              className="w-full text-sm font-sans border border-border rounded-sm px-2.5 py-2 bg-background focus:border-primary outline-none leading-relaxed resize-y"
              data-testid={`email-preview-body-${biz.id}`}
              maxLength={4000}
            />
            <span className="text-[10px] text-muted-foreground mt-0.5 block">
              {body.length} / 4000 caractères — tu peux modifier librement.
            </span>
          </label>

          <div className="bg-primary/5 border border-primary/20 rounded-sm px-3 py-2 text-[11px] leading-relaxed text-foreground/80">
            <b className="text-primary">💡 Deux façons d&apos;envoyer :</b> « Envoyer maintenant » part tout de suite depuis
            MonCommerce68 et le prospect te répond sur ton adresse. « {mobile ? "Ouvrir mon appli mail" : "Ouvrir dans Gmail"} »
            prépare le mail dans ta messagerie : il faudra cliquer toi-même sur Envoyer.
            Si Gmail s&apos;ouvre sans le message, utilise les boutons « Copier » ci-dessous.
          </div>

          <div className="flex flex-wrap gap-1.5" data-testid={`email-copy-row-${biz.id}`}>
            {[
              { id: "to", label: "Copier l'adresse e-mail", value: contactEmail, testid: "email-copy-to", done: "Adresse e-mail copiée" },
              { id: "subject", label: "Copier l'objet", value: subject, testid: "email-copy-subject", done: "Objet copié" },
              { id: "body", label: "Copier le message", value: body, testid: "email-copy-body", done: "Message copié" },
            ].map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(c.value); toast.success(c.done); }
                  catch { toast.error("Copie impossible — sélectionne le texte à la main"); }
                }}
                className="inline-flex items-center gap-1 text-[11px] font-semibold border border-border bg-card hover:bg-muted rounded-sm px-2 py-1"
                data-testid={`${c.testid}-${biz.id}`}
              >
                <Copy size={11} weight="bold"/> {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="px-4 sm:px-5 py-3 border-t border-border flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-2 bg-muted/30">
          <button
            type="button"
            onClick={reset}
            className="text-xs text-muted-foreground hover:text-foreground font-semibold inline-flex items-center justify-center gap-1 px-2 py-2"
            data-testid={`email-preview-reset-${biz.id}`}
          >
            ↺ Réinitialiser le modèle
          </button>
          <div className="flex flex-wrap gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="bg-card text-foreground border border-border hover:bg-accent px-3 py-2 rounded-sm text-xs font-semibold"
              data-testid={`email-preview-cancel-${biz.id}`}
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={openMailApp}
              disabled={!subject.trim() || !body.trim()}
              className="bg-card text-foreground border border-border hover:bg-accent px-3 py-2 rounded-sm text-xs font-semibold disabled:opacity-50"
              data-testid={`email-preview-gmail-${biz.id}`}
            >
              {mobile ? "Ouvrir mon appli mail" : "Ouvrir dans Gmail"}
            </button>
            <button
              type="button"
              onClick={sendDirect}
              disabled={sending || !subject.trim() || !body.trim()}
              className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-2 rounded-sm text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`email-preview-send-${biz.id}`}
            >
              <PaperPlaneTilt size={13} weight="bold"/> {sending ? "Envoi…" : "Envoyer maintenant"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
